import { useCallback, useEffect, useRef, useState } from 'react';
import type { Message } from '../types/chat';

// Backend ids are Mongo ObjectIds; optimistic/thinking entries use temp ids
// that must never be sent as pagination cursors
const PERSISTED_ID_RE = /^[0-9a-fA-F]{24}$/;

export interface MessagePage {
  messages: any[];
  pagination?: { has_more_older?: boolean; has_more?: boolean; has_more_newer?: boolean };
  cursor_fallback?: boolean;
}

export type FetchMessagePage = (opts: {
  limit: number;
  beforeMessageId?: string;
  afterMessageId?: string;
}) => Promise<MessagePage>;

/**
 * A self-contained, cursor-paginated message window — the same
 * live/window/jump behaviour the active chat has in useDevAgent, but as a
 * standalone list driven purely by an injected fetch. Nothing external mutates
 * it (no WebSocket streaming), so no append guard is needed; used for the
 * read-only "View Original Chat" segment view.
 */
export function useMessageWindow(fetchPage: FetchMessagePage, mapMessage: (raw: any) => Message) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [hasMoreOlder, setHasMoreOlder] = useState(false);
  // 'live': the list ends at the segment's true tail. 'window': a historical
  // slice from a navigator jump — newer messages exist below it.
  const [viewMode, setViewMode] = useState<'live' | 'window'>('live');
  const messagesRef = useRef<Message[]>([]);
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  const applyNewerEdge = useCallback((batch: Message[], hasMoreNewer: boolean) => {
    setViewMode(hasMoreNewer && batch.length > 0 ? 'window' : 'live');
  }, []);

  const reset = useCallback(() => {
    setMessages([]);
    setHasMoreOlder(false);
    setViewMode('live');
  }, []);

  // Latest page — replaces the list and returns to the live view
  const loadLatest = useCallback(async (limit = 20): Promise<Message[]> => {
    const data = await fetchPage({ limit });
    const batch = (data.messages || []).map(mapMessage);
    setMessages(batch);
    setHasMoreOlder(data.pagination?.has_more_older ?? data.pagination?.has_more ?? false);
    setViewMode('live');
    return batch;
  }, [fetchPage, mapMessage]);

  // Prepend the page older than the current oldest message (scroll up)
  const loadOlder = useCallback(async (limit = 20): Promise<void> => {
    const oldest = messagesRef.current[0];
    if (!oldest) return;
    const data = await fetchPage({ limit, beforeMessageId: oldest.id });
    const batch = (data.messages || []).map(mapMessage);
    if (batch.length > 0) {
      setMessages(prev => {
        const ids = new Set(batch.map((m: Message) => m.id));
        return [...batch, ...prev.filter(m => !ids.has(m.id))];
      });
    }
    setHasMoreOlder(data.pagination?.has_more_older ?? data.pagination?.has_more ?? false);
  }, [fetchPage, mapMessage]);

  // Jump: load a window starting AT the target message and running newer
  const loadFrom = useCallback(async (
    messageId: string,
    limit = 20,
  ): Promise<{ found: boolean } | null> => {
    try {
      const data = await fetchPage({ limit, afterMessageId: messageId });
      const batch = (data.messages || []).map(mapMessage);
      setMessages(batch);
      setHasMoreOlder(data.pagination?.has_more_older ?? false);
      applyNewerEdge(batch, data.pagination?.has_more_newer ?? false);
      const found = !data.cursor_fallback && batch.some((m: Message) => m.id === messageId);
      return { found };
    } catch (error) {
      console.error('❌ Error loading messages from target:', error);
      return null;
    }
  }, [fetchPage, mapMessage, applyNewerEdge]);

  // Append the page newer than the current tail (scroll down within a window)
  const loadNewer = useCallback(async (
    limit = 20,
  ): Promise<{ hasMoreNewer: boolean } | null> => {
    const cursor = [...messagesRef.current].reverse().find(m => PERSISTED_ID_RE.test(m.id));
    if (!cursor) return null;
    try {
      const data = await fetchPage({ limit, afterMessageId: cursor.id });
      const batch = (data.messages || []).map(mapMessage);
      if (batch.length > 0) {
        setMessages(prev => {
          const ids = new Set(batch.map((m: Message) => m.id));
          return [...prev.filter(m => !ids.has(m.id)), ...batch];
        });
      }
      const hasMoreNewer = data.pagination?.has_more_newer ?? false;
      applyNewerEdge(batch, hasMoreNewer);
      return { hasMoreNewer };
    } catch (error) {
      console.error('❌ Error loading newer messages:', error);
      return null;
    }
  }, [fetchPage, mapMessage, applyNewerEdge]);

  return { messages, hasMoreOlder, viewMode, reset, loadLatest, loadOlder, loadFrom, loadNewer };
}
